import DeleteIcon from '@mui/icons-material/DeleteOutline';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import ListItemText from "@mui/material/ListItemText";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import {styled, Theme, useMediaQuery} from "@mui/material";
import {PageTab} from "app/routes/entity-administration/tabs";
import {CanceledError} from 'axios';
import Guid from "common/values/guid/guid";
import AdministratorInvitation from "legal-entities/entities/administrator-invitation/administrator-invitation";
import AdministratorInvitationAPIService
  from "legal-entities/entities/administrator-invitation/api/administrator-invitation-api-service";

import LegalEntity from "legal-entities/entities/legal-entity/legal-entity";
import {InvitationStatus} from 'legal-entities/values/invitation-status';

import {
  createMRTColumnHelper,
  MaterialReactTable,
  type MRT_ColumnFiltersState as ColumnFiltersState,
  type MRT_PaginationState as PaginationState,
  MRT_ShowHideColumnsButton as ShowHideColumnsButton,
  type MRT_SortingState as SortingState,
  type MRT_TableInstance as TableInstance,
  MRT_ToggleDensePaddingButton as ToggleDensePaddingButton,
  MRT_ToggleFiltersButton as ToggleFiltersButton,
  MRT_ToggleFullScreenButton as ToggleFullScreenButton,
  MRT_ToggleGlobalFilterButton as ToggleGlobalFilterButton,
  useMaterialReactTable
} from "material-react-table";

import * as React from "react";
import {JSX, ReactNode, useEffect} from "react";
import {useLoaderData, useNavigate} from "react-router-dom";

import {ConfirmResponse, useConfirmDialog} from "app/providers/confirm-dialog";
import {enqueueSnackbar} from "notistack";
import {useSession} from "users/session/session-context";
import {NotAuthorized} from 'common/components/not-authorized';


const ToolbarButtonsContainer = styled("div")(({ theme }) => ({
  [theme.breakpoints.down('md')]: {
    flexDirection: "row",
    justifyContent: "space-between",
    width: "100%",
  },
  display: "flex",
  alignItems: "end",
  flexDirection: "column",
  gap: theme.spacing(1),
}));
const ToolbarButtons = styled("div")(() => ({
  display: "flex",
  alignItems: "end",
  flexWrap: "nowrap",
}));
const NameCellContainer = styled(Box)(() => ({
  display: 'flex',
  alignItems: 'center'
}));
const NameText = styled(ListItemText)(({ theme }) => ({
  marginLeft: theme.spacing(1)
}));
const RowTitle = styled(Typography)(() => ({
  fontWeight: "500",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
}));
const NewButton = styled(IconButton)(() => ({
  padding: 0
}));


type AdministratorInvitationsTableProps = {
  renderTopToolbarCustomActions: (props: {
    table: TableInstance<AdministratorInvitation>,
    additional?: JSX.Element
  }
  ) => ReactNode
};

export default function AdministratorInvitationsTable(
  props: Readonly<AdministratorInvitationsTableProps>
) {
  const { renderTopToolbarCustomActions } = props;
  const [isLoading, setIsLoading] = React.useState(false);
  const [isRefetching, setIsRefetching] = React.useState(false);
  const [query, setQuery] = React.useState("");
  const [data, setData] = React.useState<AdministratorInvitation[]>([]);
  const [rowCount, setRowCount] = React.useState(0);
  const [wasErrorLoading, setWasErrorLoading] = React.useState(false);
  const [columnFilters, setColumnFilters] =
    React.useState<ColumnFiltersState>([]);
  const [sorting, setSorting] = React.useState<SortingState>([]);
  const [pagination, setPagination] = React.useState<PaginationState>({
    pageIndex: 0,
    pageSize: 10,
  });

  const routeParams = useLoaderData();
  const session = useSession();
  const confirm = useConfirmDialog();
  const navigate = useNavigate();
  const isMediumDisplaySize = useMediaQuery((theme: Theme) =>
    theme.breakpoints.down("md")
  );

  // Handle URL changes
  useEffect(() => {
    if (!session.user?.companyEntityId || !session.user?.isCompanyManager) return;
    let abortController = new AbortController();
    void fetchData(routeParams.tab as PageTab, abortController);

    return () => {
      abortController.abort();
      abortController = new AbortController();
    }
  }, [routeParams]);

  const fetchData = async (tab?: PageTab, abortController?: AbortController) => {
    if (!session?.hasEntity) {
      setData([]);
      setRowCount(0);
      setIsLoading(false);
      setIsRefetching(false);
      setWasErrorLoading(true);
      return;
    }

    if (!data.length) {
      setIsLoading(true);
    } else {
      setIsRefetching(true);
    }

    try {
      if (!session?.isLoggedInAsAdmin) {
        setData([]);
        setRowCount(0);
        setIsLoading(false);
        setIsRefetching(false);
        setWasErrorLoading(true);
        return;
      }

      const firstUserEntityInfo = session.entities[0];
      const legalEntity = new LegalEntity(
        firstUserEntityInfo.entityId,
        firstUserEntityInfo.entityName
      );
      const adminService = new AdministratorInvitationAPIService(session);
      const invitations = await adminService.getAdminInvitationsForLegalEntity(
        legalEntity,
        abortController
      );
      setData(invitations);
      setRowCount(invitations.length);
      setIsLoading(false);
      setIsRefetching(false);
      setWasErrorLoading(false);
    } catch (error) {
      if (error instanceof CanceledError) return;
      console.error(error);
      setData([]);
      setRowCount(0);
      setWasErrorLoading(true);
      setIsLoading(false);
      setIsRefetching(false);
    }

    setWasErrorLoading(false);
    setIsLoading(false);
    setIsRefetching(false);
  };


  function renderNameColumn(data: AdministratorInvitation): React.ReactNode {
    const invitation: AdministratorInvitation = data;
    const name = `${invitation.registrationInfo?.name?.firstName} ${invitation.registrationInfo?.name?.lastName}`;
    const email = invitation.registrationInfo?.email?.value;
    const contactLink = (
      <RowTitle>{name}</RowTitle>
    );

    return (
      <NameCellContainer>
        <NameText
          primary={contactLink}
          secondary={email}
        />
      </NameCellContainer>
    );
  }

  function renderStatusColumn(data: AdministratorInvitation): React.ReactNode {
    return data?.status?.valueOf() ?? "Unknown";
  }

  async function handleCancelInvite(invitation?: AdministratorInvitation) {
    if (!invitation) return;

    const adminName = `${invitation.registrationInfo.name.firstName} ${invitation.registrationInfo.name.lastName}`;

    const response = await confirm({
      title: "Cancel Administrator Invitation?",
      message: `Cancel ${adminName}'s administrator invitation to your organization?`,
    });

    if (response === ConfirmResponse.Cancel) return;

    try {
      const service = new AdministratorInvitationAPIService(session);
      await service.cancelAdministratorInvitation(invitation);
      enqueueSnackbar("Canceled invitation", { variant: "success" });
    } catch (error) {
      console.error(error);
      enqueueSnackbar("Failed to cancel invitation", { variant: "error" });
    }
  }

  const membersColumnHelper = createMRTColumnHelper<AdministratorInvitation>();

  const columns = [
    membersColumnHelper.accessor(renderNameColumn, {
      header: "Name",
      id: "name",
    }),
    membersColumnHelper.accessor(renderStatusColumn, {
      header: "Status",
      id: "status",
    })
  ];

  const table = useMaterialReactTable({
    columns,
    data,
    enableRowSelection: false,
    enableTableHead: true,
    enableRowActions: true,
    enableColumnPinning: true,
    initialState: {
      columnPinning: { right: ["mrt-row-actions"] },
    },
    getRowId: (row) => row.id?.toString() ?? Guid.generate().toString(),
    manualFiltering: true,
    enableStickyHeader: true,
    enableStickyFooter: true,
    enableColumnFilters: true,
    manualPagination: true,
    manualSorting: true,
    onColumnFiltersChange: setColumnFilters,
    onGlobalFilterChange: setQuery,
    onPaginationChange: setPagination,
    onSortingChange: setSorting,
    renderTopToolbarCustomActions: renderTopToolbarCustomActions,
    renderToolbarInternalActions: ({ table }) => (
      <>
        {!isMediumDisplaySize && (
          <ToolbarButtonsContainer>
            <ToolbarButtons>
              <ToggleGlobalFilterButton table={table} />
              <ToggleFiltersButton table={table} />
              <ShowHideColumnsButton table={table} />
              <ToggleDensePaddingButton table={table} />
              <ToggleFullScreenButton table={table} />
            </ToolbarButtons>
            <Button
              startIcon={<PersonAddIcon />}
              onClick={() => navigate(`/administration/invitations/invite`)}>
              Invite Users
            </Button>
          </ToolbarButtonsContainer>
        )}
        {isMediumDisplaySize && (
          <NewButton
            color="primary"
            onClick={() => navigate(`/administration/invitations/invite`)}
          >
            <PersonAddIcon fontSize='large' />
          </NewButton>
        )}
      </>
    ),
    renderRowActions: ({ row }) => {
      const data: AdministratorInvitation = row.original;
      return (
        <ToolbarButtons>
          <Tooltip title="Cancel Invitation">
            <span>
              <IconButton
                disabled={data.status !== InvitationStatus.Open}
                onClick={() => handleCancelInvite(data)}
              >
                <DeleteIcon color={data.status === InvitationStatus.Open ? "error" : "disabled"} />
              </IconButton>
            </span>
          </Tooltip>
        </ToolbarButtons>
      );
    },
    muiTableContainerProps: ({ table }) => ({
      sx: {
        height: `calc(100% - ${table.refs.topToolbarRef.current?.offsetHeight}px - ${table.refs.bottomToolbarRef.current?.offsetHeight}px)`,
      },
    }),
    muiTablePaperProps: {
      sx: {
        height: "100%",
      },
    },
    muiToolbarAlertBannerProps: wasErrorLoading
      ? {
        color: "error",
        children: "Error loading data",
      }
      : undefined,
    rowCount,
    state: {
      columnFilters,
      globalFilter: query,
      isLoading,
      pagination,
      showAlertBanner: wasErrorLoading,
      showProgressBars: isRefetching,
      sorting,
    },
  });

  if (!session.user?.companyEntityId || !session.user?.isCompanyManager) {
    return <NotAuthorized />;
  }

  return <MaterialReactTable table={table} />;
}
